import { useEffect, useRef } from 'react';

const HeroSection = () => {
  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!gridRef.current) return;
      const x = (e.clientX / window.innerWidth) * 20 - 10;
      const y = (e.clientY / window.innerHeight) * 20 - 10;
      gridRef.current.style.transform = `translate(${x}px, ${y}px)`;
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const handleScroll = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-start overflow-hidden bg-[#08080c]"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/69f7e021483c1a5f9c0283c47d4db2e3.jpg"
          alt="ELLE NOLO EQUIPMENT motorsport hero"
          className="w-full h-full object-cover object-top opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#08080c] via-[#08080c]/70 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#08080c] via-transparent to-[#08080c]/50" />
      </div>

      {/* Animated Grid */}
      <div
        ref={gridRef}
        className="absolute inset-0 transition-transform duration-100 ease-out"
        style={{
          backgroundImage: `
            linear-gradient(rgba(220,38,38,0.06) 1px, transparent 1px),
            linear-gradient(90deg, rgba(220,38,38,0.06) 1px, transparent 1px)
          `,
          backgroundSize: '80px 80px',
        }}
      />

      {/* Glowing Lines */}
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-red-600/50 to-transparent animate-pulse" />
      <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-orange-500/30 to-transparent" />

      {/* Content */}
      <div className="relative z-10 px-6 md:px-16 lg:px-24 w-full max-w-5xl">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-1.5 border border-red-700/50 rounded-full mb-8 bg-red-950/20">
          <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
          <span className="font-['Exo_2'] text-xs text-red-400 tracking-[0.3em] uppercase">
            Firenze · Europa · Racing
          </span>
        </div>

        {/* Main Title */}
        <h1 className="font-['Exo_2'] font-black text-white leading-none mb-6">
          <span className="block text-5xl md:text-7xl lg:text-8xl tracking-tight hero-glitch">
            MOTORSPORT
          </span>
          <span className="block text-5xl md:text-7xl lg:text-8xl tracking-tight text-red-500">
            EQUIPMENT
          </span>
          <span className="block text-5xl md:text-7xl lg:text-8xl tracking-tight text-gray-400">
            RENTAL
          </span>
        </h1>

        {/* Subtitle */}
        <p className="font-['Inter'] text-gray-400 text-base md:text-lg max-w-xl mb-10 leading-relaxed">
          Noleggio professionale di attrezzature per motorsport, eventi fieristici, catering e ricambistica auto.
          Attivi in tutta Europa.
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={() => handleScroll('#servizi')}
            className="group relative px-8 py-4 bg-red-600 hover:bg-red-500 text-white font-['Exo_2'] font-bold text-sm tracking-widest uppercase rounded-md transition-all duration-300 overflow-hidden whitespace-nowrap cursor-pointer"
          >
            <span className="relative z-10">Scopri i Servizi</span>
            <div className="absolute inset-0 bg-gradient-to-r from-red-600 to-orange-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </button>
          <button
            onClick={() => handleScroll('#contatti')}
            className="px-8 py-4 border border-gray-600 hover:border-red-500 text-gray-300 hover:text-white font-['Exo_2'] font-bold text-sm tracking-widest uppercase rounded-md transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            Contattaci
          </button>
        </div>

        {/* Stats */}
        <div className="flex flex-wrap gap-8 mt-16">
          {[
            { value: '5+', label: 'Settori Serviti' },
            { value: '100%', label: 'Attrezzature Testate' },
            { value: 'EU', label: 'Copertura Europea' },
          ].map((stat) => (
            <div key={stat.label} className="flex flex-col">
              <span className="font-['Exo_2'] font-black text-3xl text-red-500">{stat.value}</span>
              <span className="font-['Inter'] text-xs text-gray-500 tracking-widest uppercase mt-1">{stat.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <span className="font-['Exo_2'] text-xs text-gray-600 tracking-widest uppercase">Scroll</span>
        <div className="w-4 h-4 flex items-center justify-center">
          <i className="ri-arrow-down-line text-gray-600 text-sm" />
        </div>
      </div>

      <style>{`
        @keyframes glitch {
          0%, 90%, 100% { text-shadow: none; transform: none; }
          92% { text-shadow: -2px 0 #dc2626; transform: translateX(2px); }
          94% { text-shadow: 2px 0 #f97316; transform: translateX(-2px); }
          96% { text-shadow: none; transform: none; }
        }
        .hero-glitch {
          animation: glitch 6s infinite;
        }
      `}</style>
    </section>
  );
};

export default HeroSection;
